<template>
    <div class="creatives">
        <div class="creatives__header">
            <div class="creatives__titlebar">
                <h2 class="creatives__title">RTB креативы</h2>
            </div>
            <div class="creatives__meta">
                <input
                    v-model.trim="query"
                    type="text"
                    class="input input--search"
                    placeholder="Поиск по названию"
                    aria-label="Поиск по названию"
                />
                <label class="field">
                    <select v-model="selectedAdvertiserId" class="select" aria-label="Фильтр по рекламодателю">
                        <option value="">Все рекламодатели</option>
                        <option v-for="adv in advertisers" :key="adv.id" :value="adv.id">{{ adv.name }}</option>
                    </select>
                </label>
                <label class="field">
                    <select v-model="selectedBrandId" class="select" aria-label="Фильтр по бренду">
                        <option value="">Все бренды</option>
                        <option v-for="b in brandsByAdvertiser" :key="b.id" :value="b.id">{{ b.name }}</option>
                    </select>
                </label>
                <select v-model="statusFilter" class="select" aria-label="Фильтр по статусу">
                    <option value="">Все статусы</option>
                    <option v-for="(label, key) in statusDict" :key="key" :value="key">
                        {{ label }}
                    </option>
                </select>
                <select v-model="typeFilter" class="select" aria-label="Фильтр по типу">
                    <option value="">Все типы</option>
                    <option v-for="(label, key) in typeDict" :key="key" :value="key">
                        {{ label }}
                    </option>
                </select>
                <span class="muted">Всего: {{ filteredCreatives.length }}</span>
            </div>
        </div>

        <div v-if="(creatives || []).length === 0" class="creatives__empty">Нет креативов</div>
        <div v-else-if="filteredCreatives.length === 0" class="creatives__empty">Ничего не найдено</div>

        <div v-else class="creatives__grid">
            <article
                v-for="creative in filteredCreatives"
                :key="creative.id"
                class="rtb-creative-card"
            >
                <header class="rtb-creative-card__header">
                    <div class="rtb-creative-card__title-row">
                        <h3 class="rtb-creative-card__title">
                            <img :src="getTypeIcon(creative)" :alt="creative.type" class="title-type-icon" />
                            {{ creative.name }}
                        </h3>
                        <div class="rtb-creative-card__badges">
                            <span :class="['status-badge', 'status-badge--' + statusBadgeClass(creative.status)]">
                                {{ statusLabel(creative.status) }}
                            </span>
                            <span
                                v-if="readinessCoverageLabel(creative)"
                                class="status-badge status-badge--coverage"
                                :title="readinessTooltip(creative)"
                            >
                                {{ readinessCoverageLabel(creative) }}
                            </span>
                        </div>
                    </div>
                    <p class="rtb-creative-card__meta-line">
                        <span>ID {{ creative.id }}</span>
                        <span v-if="advertiserName(creative)" class="rtb-creative-card__meta-sep">·</span>
                        <span v-if="advertiserName(creative)">{{ advertiserName(creative) }}</span>
                        <span v-if="brandName(creative)" class="rtb-creative-card__meta-sep">·</span>
                        <span v-if="brandName(creative)">{{ brandName(creative) }}</span>
                    </p>
                </header>

                <div class="rtb-creative-card__variants">
                    <div
                        v-for="slot in variantSlots(creative)"
                        :key="slot.locale"
                        class="rtb-creative-card__variant"
                        :class="{ 'rtb-creative-card__variant--empty': !slot.url }"
                    >
                        <span class="locale-pill">{{ slot.locale.toUpperCase() }}</span>
                        <button
                            v-if="slot.url"
                            type="button"
                            class="rtb-creative-card__preview-btn"
                            :aria-label="`Воспроизвести ${slot.locale.toUpperCase()}`"
                            @click="openPreview(creative, slot)"
                        >
                            <video
                                v-if="isVideo(creative)"
                                :src="slot.url"
                                class="rtb-creative-card__preview-media"
                                muted
                                playsinline
                                preload="metadata"
                            />
                            <img
                                v-else
                                :src="slot.url"
                                :alt="`${creative.name} ${slot.locale}`"
                                class="rtb-creative-card__preview-media"
                            />
                        </button>
                        <div v-else class="rtb-creative-card__placeholder">
                            Нет версии {{ slot.locale.toUpperCase() }}
                        </div>
                        <p v-if="hasVariantSpecs(creative, slot)" class="rtb-creative-card__variant-specs">
                            <span v-if="hasDimensions(slot)">{{ formatDimensions(slot) }}</span>
                            <span
                                v-if="hasDimensions(slot) && showSlotDuration(creative, slot)"
                                class="rtb-creative-card__meta-sep"
                            >·</span>
                            <span
                                v-if="showSlotDuration(creative, slot)"
                                class="rtb-creative-card__duration"
                                :title="formatDurationMs(slot)"
                            >{{ formatDuration(slot) }}</span>
                        </p>
                        <button
                            v-if="slot.url"
                            type="button"
                            class="btn rtb-creative-card__replace-btn"
                            :disabled="!canReplace(creative, slot) || isReplacing(creative, slot.locale)"
                            :title="replaceButtonTitle(creative, slot)"
                            @click="pickReplacement(creative, slot)"
                        >
                            {{ isReplacing(creative, slot.locale) ? 'Загрузка…' : 'Заменить' }}
                        </button>
                    </div>
                </div>

                <footer class="rtb-creative-card__footer">
                    <button
                        class="btn btn--danger"
                        @click="confirmDelete(creative)"
                        :disabled="isDeletingId === creative.id"
                    >
                        Удалить
                    </button>
                </footer>
            </article>
        </div>

        <div
            v-if="previewModal.open"
            class="modal-backdrop rtb-creative-preview-modal"
            role="dialog"
            aria-modal="true"
            @click.self="closePreview"
        >
            <div class="modal rtb-creative-preview-modal__dialog">
                <div class="modal__title">
                    {{ previewModal.name }}
                    <span class="locale-pill">{{ previewModal.locale }}</span>
                </div>
                <div class="modal__body rtb-creative-preview-modal__body">
                    <video
                        v-if="previewModal.isVideo"
                        ref="previewVideo"
                        :src="previewModal.url"
                        class="rtb-creative-preview-modal__video"
                        controls
                        autoplay
                        playsinline
                    />
                    <img
                        v-else
                        :src="previewModal.url"
                        :alt="previewModal.name"
                        class="rtb-creative-preview-modal__image"
                    />
                </div>
                <div class="modal__actions">
                    <button class="btn" @click="closePreview">Закрыть</button>
                </div>
            </div>
        </div>

        <div v-if="showReplaceConfirm" class="modal-backdrop" role="dialog" aria-modal="true">
            <div class="modal">
                <div class="modal__title">Заменить {{ replaceTarget?.locale?.toUpperCase() }} версию?</div>
                <div class="modal__body">
                    <div v-if="replaceTarget" class="rtb-creative-replace-meta">
                        <div class="rtb-creative-replace-meta__name">{{ replaceTarget.file.name }}</div>
                        <div class="rtb-creative-replace-meta__specs">
                            <span v-if="hasDimensions(replaceTarget)">{{ formatDimensions(replaceTarget) }}</span>
                            <span
                                v-if="hasDimensions(replaceTarget) && showReplaceDuration"
                                class="rtb-creative-card__meta-sep"
                            >·</span>
                            <span
                                v-if="showReplaceDuration"
                                class="rtb-creative-card__duration"
                                :title="formatDurationMs(replaceTarget)"
                            >{{ replaceDurationHint }}</span>
                        </div>
                        <p v-if="replaceDurationError" class="rtb-creative-replace-meta__error">
                            {{ replaceDurationError }}
                        </p>
                    </div>
                    <p>Замена сбросит модерацию этой версии во всех SSP. Вторая версия не изменится.</p>
                </div>
                <div class="modal__actions">
                    <button class="btn" @click="cancelReplace">Отмена</button>
                    <button
                        class="btn btn--primary"
                        :disabled="Boolean(replaceDurationError)"
                        @click="doReplace"
                    >
                        Заменить
                    </button>
                </div>
            </div>
        </div>

        <input
            ref="replaceFileInput"
            type="file"
            accept="image/*,video/*"
            class="rtb-creative-card__replace-input"
            @change="onReplacementFile"
        />

        <div v-if="showConfirm" class="modal-backdrop" role="dialog" aria-modal="true">
            <div class="modal">
                <div class="modal__title">Удалить креатив?</div>
                <div class="modal__body">
                    Креатив будет удалён на платформе (soft delete). Это действие нельзя отменить из интерфейса.
                </div>
                <div class="modal__actions">
                    <button class="btn" @click="cancelModal">Отмена</button>
                    <button class="btn btn--danger" @click="doDelete">Удалить</button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import axios from 'axios';
import { useToast } from '../../composables/useToast';
import { notifyHttpError } from '../../utils/httpErrors';
import {
    getCreativeStatusLabel,
    getCreativeStatusBadgeClass,
    getReadinessCoverageLabel,
    buildMissingTargetsTooltip,
} from '../../utils/creativeStatus';
import {
    DURATION_MATCH_TOLERANCE_MS,
    DURATION_MAX_MS,
    DURATION_MIN_MS,
    formatDimensions,
    formatDuration,
    formatDurationMs,
    formatDurationNormalizeHint,
    hasDimensions,
    hasDuration,
    mediaKindFromFile,
    nearestDurationMs,
    readMediaMeta,
} from '../../utils/mediaMeta';

const LOCALE_SLOTS = ['ru', 'kz'];

export default {
    name: 'RtbCreatives',
    props: {
        creatives: {
            type: Array,
            required: true,
        },
        brands: {
            type: Array,
            default: () => [],
        },
        user: {
            type: Object,
            required: true,
        },
    },
    data() {
        return {
            query: '',
            statusFilter: '',
            typeFilter: '',
            selectedAdvertiserId: '',
            selectedBrandId: '',
            showConfirm: false,
            creativeToDelete: null,
            isDeletingId: null,
            showReplaceConfirm: false,
            replacePick: null,
            replaceTarget: null,
            isReplacingKey: null,
            previewModal: {
                open: false,
                url: '',
                locale: '',
                name: '',
                isVideo: true,
            },
            statusDict: {
                approve: 'На модерации',
                review: 'На модерации',
                pending_approval: 'На модерации',
                sent_to_ssp: 'На стороне SSP',
                confirmed: 'Подтверждён',
                rejected: 'Отклонён',
                cancel: 'Отменён',
                cancelled: 'Отменён',
            },
            typeDict: {
                picture: 'Изображение',
                video: 'Видео',
            },
        };
    },
    computed: {
        advertisers() {
            const advMap = new Map();
            for (const b of this.brands || []) {
                const adv = b && b.advertiser;
                if (adv && adv.id) {
                    if (!advMap.has(Number(adv.id))) {
                        advMap.set(Number(adv.id), { id: adv.id, name: adv.name });
                    }
                } else if (b && b.advertiser_id) {
                    if (!advMap.has(Number(b.advertiser_id))) {
                        advMap.set(Number(b.advertiser_id), {
                            id: b.advertiser_id,
                            name: b.advertiser?.name || `Рекламодатель ${b.advertiser_id}`,
                        });
                    }
                }
            }
            return Array.from(advMap.values());
        },
        brandsByAdvertiser() {
            const selAdv = this.selectedAdvertiserId ? Number(this.selectedAdvertiserId) : null;
            if (!selAdv) return this.brands || [];
            return (this.brands || []).filter((b) => Number(b.advertiser_id) === selAdv);
        },
        creativesSafe() {
            return Array.isArray(this.creatives) ? this.creatives : [];
        },
        showReplaceDuration() {
            const target = this.replaceTarget;
            return Boolean(target) && this.isVideo(target.creative) && hasDuration(target);
        },
        replaceSibling() {
            const target = this.replaceTarget;
            if (!target?.creative || !target.locale) return null;
            const other = String(target.locale).toLowerCase() === 'ru' ? 'kz' : 'ru';
            return this.variantByLocale(target.creative, other);
        },
        replaceDurationTargetMs() {
            const sibling = this.replaceSibling;
            if (!sibling || sibling.duration == null) return null;
            return nearestDurationMs(sibling.duration);
        },
        replaceDurationHint() {
            const target = this.replaceTarget;
            if (!target || !this.showReplaceDuration) return '';
            const canonical = this.replaceDurationTargetMs;
            const hint = formatDurationNormalizeHint(target.duration, canonical ?? nearestDurationMs(target.duration));
            return hint || formatDuration(target);
        },
        replaceDurationError() {
            if (!this.showReplaceDuration) return '';
            const target = this.replaceTarget;
            const canonical = this.replaceDurationTargetMs;
            if (canonical == null) {
                return 'Для сверки длительности нужна вторая языковая версия';
            }
            if (canonical < DURATION_MIN_MS || canonical > DURATION_MAX_MS) {
                return 'Длительность видео должна быть от 1 до 60 с';
            }
            if (Math.abs(Number(target.duration) - canonical) > DURATION_MATCH_TOLERANCE_MS) {
                return 'Длительность RU и KZ версий должна совпадать';
            }
            return '';
        },
        filteredCreatives() {
            const q = (this.query || '').toLowerCase();
            const status = (this.statusFilter || '').toLowerCase();
            const type = (this.typeFilter || '').toLowerCase();
            const selAdvertisers = this.selectedAdvertiserId ? [Number(this.selectedAdvertiserId)] : [];
            const selBrands = this.selectedBrandId ? [Number(this.selectedBrandId)] : [];

            return this.creativesSafe.filter((cr) => {
                const nameOk = q
                    ? String(cr.name || '').toLowerCase().includes(q)
                    : true;
                const statusOk = status ? this.normalizeStatus(cr.status) === status : true;
                const typeOk = type
                    ? this.normalizeType(cr.type) === type || (type === 'video' && this.isVideo(cr))
                    : true;

                const brandId = cr.brand_id ?? cr.brandId ?? cr.brand?.id ?? null;
                let advertiserId = cr.advertiser_id ?? cr.advertiserId ?? cr.advertiser?.id ?? null;
                if (!advertiserId && brandId) {
                    const b = this.brandById(brandId);
                    if (b) {
                        advertiserId = b.advertiser?.id ?? b.advertiser_id ?? null;
                    }
                }
                if (!advertiserId && cr.brand?.advertiser?.id) {
                    advertiserId = cr.brand.advertiser.id;
                }

                let advertiserOk = true;
                let brandOk = true;

                if (selAdvertisers.length) {
                    advertiserOk = advertiserId ? Number(advertiserId) === selAdvertisers[0] : false;
                }
                if (selBrands.length) {
                    brandOk = brandId ? Number(brandId) === selBrands[0] : false;
                }

                return nameOk && statusOk && typeOk && advertiserOk && brandOk;
            });
        },
    },
    watch: {
        selectedAdvertiserId() {
            this.syncSelectedBrand();
        },
    },
    mounted() {
        document.addEventListener('keydown', this.onEscapeKey);
    },
    beforeUnmount() {
        document.removeEventListener('keydown', this.onEscapeKey);
    },
    methods: {
        brandById(id) {
            if (!id) return null;
            return (this.brands || []).find((b) => Number(b.id) === Number(id)) || null;
        },
        syncSelectedBrand() {
            const validBrandIds = (this.brandsByAdvertiser || []).map((b) => String(b.id));
            if (!this.selectedBrandId) return;
            if (!validBrandIds.includes(String(this.selectedBrandId))) {
                this.selectedBrandId = '';
            }
        },
        normalizeStatus(s) {
            if (!s) return '';
            return String(s).toLowerCase().trim();
        },
        normalizeType(t) {
            const k = String(t || '').toLowerCase();
            if (k.startsWith('image')) return 'picture';
            if (k.startsWith('video')) return 'video';
            return k;
        },
        statusLabel(s) {
            return getCreativeStatusLabel(this.normalizeStatus(s));
        },
        statusBadgeClass(s) {
            return getCreativeStatusBadgeClass(this.normalizeStatus(s));
        },
        readinessCoverageLabel(creative) {
            return getReadinessCoverageLabel(creative?.readiness_details);
        },
        readinessTooltip(creative) {
            return buildMissingTargetsTooltip(creative?.readiness_details);
        },
        isVideo(creative) {
            if (!creative) return false;
            const type = String(creative.type || '').toLowerCase();
            if (type.includes('video')) return true;
            const url = String(creative.url || '').toLowerCase();
            return /(\.mp4|\.webm|\.ogg|\.ogv|\.mov|\.m4v)(\?.*)?$/.test(url);
        },
        variantByLocale(creative, locale) {
            const variants = Array.isArray(creative?.variants) ? creative.variants : [];
            const found = variants.find((v) => String(v.locale || '').toLowerCase() === locale);
            if (found?.url) return found;
            if (locale === 'ru' && creative?.url) {
                return {
                    locale: 'ru',
                    url: creative.url,
                    width: creative.width,
                    height: creative.height,
                    duration: creative.duration,
                };
            }
            return { locale, url: null };
        },
        variantSlots(creative) {
            return LOCALE_SLOTS.map((locale) => this.variantByLocale(creative, locale));
        },
        hasDimensions,
        formatDimensions,
        formatDuration,
        formatDurationMs,
        hasVariantSpecs(creative, slot) {
            return Boolean(slot?.url) && (hasDimensions(slot) || this.showSlotDuration(creative, slot));
        },
        showSlotDuration(creative, slot) {
            return this.isVideo(creative) && hasDuration(slot);
        },
        getTypeIcon(creative) {
            return this.isVideo(creative) ? '/images/video.svg' : '/images/picture.svg';
        },
        brandName(creative) {
            if (!creative) return '';
            if (creative.brand && typeof creative.brand === 'object') {
                return creative.brand.name || '';
            }
            const bid = creative.brand_id ?? creative.brandId ?? null;
            const b = this.brandById(bid);
            if (b) return b.name;
            if (typeof creative.brand === 'string') return creative.brand;
            return '';
        },
        advertiserName(creative) {
            if (!creative) return '';
            if (creative.brand?.advertiser?.name) {
                return creative.brand.advertiser.name;
            }
            const bid = creative.brand_id ?? creative.brandId ?? creative.brand?.id ?? null;
            const b = this.brandById(bid);
            if (b?.advertiser?.name) return b.advertiser.name;
            if (creative.advertiser && typeof creative.advertiser === 'object') {
                return creative.advertiser.name || '';
            }
            return creative.advertiser_name || creative.advertiserName || '';
        },
        openPreview(creative, slot) {
            if (!slot?.url) return;
            this.previewModal = {
                open: true,
                url: slot.url,
                locale: String(slot.locale || '').toUpperCase(),
                name: creative.name || 'Креатив',
                isVideo: this.isVideo(creative),
            };
            this.$nextTick(() => {
                const el = this.$refs.previewVideo;
                if (el && typeof el.play === 'function') {
                    el.play().catch(() => {});
                }
            });
        },
        closePreview() {
            const el = this.$refs.previewVideo;
            if (el && typeof el.pause === 'function') {
                el.pause();
            }
            this.previewModal = {
                open: false,
                url: '',
                locale: '',
                name: '',
                isVideo: true,
            };
        },
        onEscapeKey(event) {
            if (event.key !== 'Escape') return;
            if (this.previewModal.open) {
                this.closePreview();
            } else if (this.showReplaceConfirm) {
                this.cancelReplace();
            } else if (this.showConfirm) {
                this.cancelModal();
            }
        },
        localeReplaceState(creative, locale) {
            const key = String(locale || '').toLowerCase();
            const fromMap = creative?.locale_replace?.[key];
            if (fromMap && typeof fromMap.can_replace === 'boolean') {
                return fromMap;
            }
            const variants = Array.isArray(creative?.variants) ? creative.variants : [];
            const variant = variants.find((item) => String(item.locale || '').toLowerCase() === key);
            if (variant && typeof variant.can_replace === 'boolean') {
                return {
                    can_replace: variant.can_replace,
                    replace_blocked_reason: variant.replace_blocked_reason || '',
                };
            }
            return { can_replace: false, replace_blocked_reason: '' };
        },
        canReplace(creative, slot) {
            return this.localeReplaceState(creative, slot?.locale).can_replace === true;
        },
        replaceButtonTitle(creative, slot) {
            const state = this.localeReplaceState(creative, slot?.locale);
            if (state.can_replace) {
                return 'Заменить файл этой версии';
            }
            return state.replace_blocked_reason || 'Сейчас нельзя заменить этот файл';
        },
        isReplacing(creative, locale) {
            return this.isReplacingKey === `${creative.id}:${String(locale || '').toLowerCase()}`;
        },
        pickReplacement(creative, slot) {
            if (!this.canReplace(creative, slot) || this.isReplacing(creative, slot.locale)) {
                return;
            }
            this.replacePick = {
                creative,
                locale: String(slot.locale || '').toLowerCase(),
            };
            const input = this.$refs.replaceFileInput;
            if (input) {
                input.value = '';
                input.click();
            }
        },
        async onReplacementFile(event) {
            const file = event.target.files?.[0];
            const pick = this.replacePick;
            event.target.value = '';
            this.replacePick = null;
            if (!file || !pick?.creative) {
                return;
            }

            try {
                const expectedVideo = this.isVideo(pick.creative);
                const isVideo = mediaKindFromFile(file) === 'video';
                if (isVideo !== expectedVideo) {
                    throw new Error(
                        expectedVideo
                            ? 'Для этого креатива нужен видеофайл'
                            : 'Для этого креатива нужен файл изображения',
                    );
                }

                const meta = await readMediaMeta(file);
                const current = this.variantByLocale(pick.creative, pick.locale);
                this.replaceTarget = {
                    creative: pick.creative,
                    locale: pick.locale,
                    file,
                    duration: meta.duration ?? Number(current?.duration ?? pick.creative.duration ?? 0),
                    width: meta.width,
                    height: meta.height,
                };
                this.showReplaceConfirm = true;
            } catch (error) {
                useToast().error(error.message || 'Не удалось прочитать файл');
            }
        },
        cancelReplace() {
            this.showReplaceConfirm = false;
            this.replaceTarget = null;
        },
        applyReplaceResponse(creative, locale, data) {
            if (typeof data.creative_status === 'string') {
                creative.status = data.creative_status;
            }
            if (data.readiness_details && typeof data.readiness_details === 'object') {
                creative.readiness_details = data.readiness_details;
            }
            if (data.locale_replace && typeof data.locale_replace === 'object') {
                creative.locale_replace = data.locale_replace;
            }

            const variant = data.variant;
            if (!variant) {
                return;
            }
            if (!Array.isArray(creative.variants)) {
                creative.variants = [];
            }

            const inspection = data.locale_replace?.[locale] || {};
            const merged = {
                ...variant,
                can_replace: inspection.can_replace,
                replace_blocked_reason: inspection.replace_blocked_reason,
            };
            const idx = creative.variants.findIndex(
                (item) => String(item.locale || '').toLowerCase() === locale,
            );
            if (idx === -1) {
                creative.variants.push(merged);
            } else {
                creative.variants.splice(idx, 1, merged);
            }

            if (locale === 'ru' && variant.url) {
                creative.url = variant.url;
                if (variant.duration != null) {
                    creative.duration = variant.duration;
                }
            }
        },
        async doReplace() {
            const target = this.replaceTarget;
            if (!target?.creative || !target.file) {
                return;
            }
            if (this.replaceDurationError) {
                return;
            }

            const locale = target.locale;
            const creative = target.creative;
            this.isReplacingKey = `${creative.id}:${locale}`;
            this.showReplaceConfirm = false;

            const formData = new FormData();
            formData.append('file', target.file);
            formData.append('duration', String(target.duration));
            formData.append('width', String(target.width));
            formData.append('height', String(target.height));
            formData.append(
                'hasAudioTrack',
                this.variantByLocale(creative, locale)?.has_audio_track ? '1' : '0',
            );

            try {
                const { data } = await axios.post(`/api/creatives/${creative.id}/variants/${locale}`, formData);
                this.applyReplaceResponse(creative, locale, data);
                useToast().success(data.message || 'Файл заменён');
            } catch (error) {
                notifyHttpError(error, 'Не удалось заменить файл');
            } finally {
                this.isReplacingKey = null;
                this.replaceTarget = null;
            }
        },
        confirmDelete(creative) {
            this.creativeToDelete = creative;
            this.showConfirm = true;
        },
        async doDelete() {
            if (!this.creativeToDelete) return;
            try {
                this.isDeletingId = this.creativeToDelete.id;
                await axios.delete(`/api/creatives/${this.creativeToDelete.id}`);
                const idx = (this.creatives || []).findIndex((x) => x.id === this.creativeToDelete.id);
                if (idx !== -1) {
                    this.creatives.splice(idx, 1);
                }
                useToast().success('Креатив удалён');
            } catch (e) {
                notifyHttpError(e, 'Не удалось удалить креатив');
            } finally {
                this.isDeletingId = null;
                this.showConfirm = false;
                this.creativeToDelete = null;
            }
        },
        cancelModal() {
            this.showConfirm = false;
            this.creativeToDelete = null;
        },
    },
};
</script>

<style scoped>
.title-type-icon {
    width: 18px;
    height: 18px;
    margin-right: 8px;
    vertical-align: middle;
    opacity: 0.8;
}

select {
    width: 165px;
}

.rtb-creative-card__replace-input {
    display: none;
}
</style>
